import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { tokenStore } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Trash2, Upload, Film, Image as ImageIcon, X } from "lucide-react";

function isVideo(ct) { return (ct || "").startsWith("video/"); }
function isImage(ct) { return (ct || "").startsWith("image/"); }
function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function fileUrl(id, opts = {}) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Atașăm token-ul ca query param: <video>/<img> nu pot trimite headerul Authorization.
  const token = tokenStore.get();
  const params = new URLSearchParams({ token: token || "" });
  if (opts.download) params.set("download", "1");
  return `${base}/api/video-explicative/${id}?${params.toString()}`;
}

export default function VideoExplicative() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]); // [{name, percent, done, error}]
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/video-explicative");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare încărcare fișiere");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onPickFiles = () => inputRef.current?.click();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(files.map((f) => ({ name: f.name, size: f.size, percent: 0, done: false, error: false })));
    let ok = 0, fail = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/video-explicative/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const total = evt.total || f.size;
            const percent = total ? Math.round((evt.loaded / total) * 100) : 0;
            setUploadProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, percent } : p));
          },
        });
        setUploadProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, percent: 100, done: true } : p));
        ok += 1;
      } catch (err) {
        console.error(err);
        setUploadProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, error: true } : p));
        fail += 1;
      }
    }
    setUploading(false);
    if (ok) toast.success(`Încărcat: ${ok} fișier${ok === 1 ? "" : "e"}`);
    if (fail) toast.error(`Eșuat: ${fail} fișier${fail === 1 ? "" : "e"}`);
    e.target.value = "";
    await load();
    // Lasă bara de progres vizibilă 2s după finalizare apoi curăță
    setTimeout(() => setUploadProgress([]), 2000);
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Sigur ștergi „${it.original_filename}"?`)) return;
    try {
      await api.delete(`/video-explicative/${it.id}`);
      toast.success("Șters");
      setPreview((p) => (p?.id === it.id ? null : p));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  return (
    <div className="space-y-5" data-testid="page-video-explicative">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold">🎬 Video explicative</h1>
          <p className="text-sm text-zinc-500">Filmări și imagini explicative — disponibile pentru toți angajații.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="video/*,image/*"
              onChange={onUpload}
              className="hidden"
              data-testid="video-explicative-file-input"
            />
            <Button
              onClick={onPickFiles}
              disabled={uploading}
              className="bg-[var(--primary)] text-white"
              data-testid="video-explicative-upload-btn"
            >
              <Upload size={16} className="mr-1.5" />
              {uploading ? "Se încarcă…" : "Încarcă fișiere"}
            </Button>
          </div>
        )}
      </div>

      {isAdmin && uploadProgress.length > 0 && (
        <div className="card-flat p-3 space-y-2" data-testid="video-explicative-upload-progress">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">Upload în curs</div>
          {uploadProgress.map((p, i) => (
            <div key={i} className="space-y-0.5">
              <div className="flex items-center justify-between text-xs gap-2">
                <span className="truncate flex-1" title={p.name}>{p.name}</span>
                <span className={`tabular-nums font-semibold ${p.error ? "text-red-600" : p.done ? "text-emerald-600" : "text-zinc-600"}`}>
                  {p.error ? "Eroare" : p.done ? "✓ Gata" : `${p.percent}%`}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden">
                <div
                  className={`h-full transition-all ${p.error ? "bg-red-500" : p.done ? "bg-emerald-500" : "bg-[var(--primary)]"}`}
                  style={{ width: `${p.percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500 italic">Se încarcă…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-8 text-center text-zinc-500">
          <Film size={32} className="mx-auto mb-2 opacity-50" />
          <div className="text-sm">Niciun fișier încărcat încă.</div>
          {isAdmin && <div className="text-xs mt-1">Apasă „Încarcă fișiere" pentru a adăuga.</div>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="video-explicative-grid">
          {items.map((it) => (
            <div
              key={it.id}
              className="card-flat overflow-hidden group hover:shadow-md transition-all cursor-pointer relative"
              data-testid={`video-explicative-item-${it.id}`}
              onClick={() => setPreview(it)}
            >
              <div className="aspect-video bg-zinc-100 flex items-center justify-center overflow-hidden">
                {isImage(it.content_type) ? (
                  <img
                    src={fileUrl(it.id)}
                    alt={it.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : isVideo(it.content_type) ? (
                  <video
                    src={fileUrl(it.id)}
                    className="w-full h-full object-cover"
                    preload="none"
                    muted
                  />
                ) : (
                  <div className="text-zinc-400 text-xs">Fișier</div>
                )}
                <div className="absolute top-1.5 left-1.5 bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                  {isVideo(it.content_type) ? <><Film size={10}/> Video</> : isImage(it.content_type) ? <><ImageIcon size={10}/> Foto</> : "Fișier"}
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(it); }}
                    className="absolute top-1.5 right-1.5 bg-red-600/90 hover:bg-red-700 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`video-explicative-delete-${it.id}`}
                    title="Șterge"
                  >
                    <Trash2 size={12}/>
                  </button>
                )}
              </div>
              <div className="p-2">
                <div className="text-xs font-semibold truncate" title={it.original_filename}>{it.original_filename}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center justify-between">
                  <span>{fmtSize(it.size)}</span>
                  <span>{fmtDate(it.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="video-explicative-preview"
        >
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            data-testid="video-explicative-preview-close"
          >
            <X size={20}/>
          </button>
          <div className="max-w-5xl w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            {isVideo(preview.content_type) ? (
              <video src={fileUrl(preview.id)} controls autoPlay className="w-full max-h-[80vh] rounded-lg" />
            ) : isImage(preview.content_type) ? (
              <img src={fileUrl(preview.id)} alt={preview.original_filename} className="w-full max-h-[80vh] object-contain rounded-lg" />
            ) : null}
            <div className="text-white/90 text-sm mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold">{preview.original_filename}</div>
                <div className="text-xs text-white/60">{fmtSize(preview.size)} · {fmtDate(preview.created_at)} · de {preview.uploaded_by_name || "—"}</div>
              </div>
              <a
                href={fileUrl(preview.id, { download: 1 })}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20"
              >
                ⬇ Descarcă
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
